<!--
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
SPDX-License-Identifier: Apache-2.0
-->

<!-- This SFC displays a filterable list of tenants with status toggle controls. -->
<!-- 🔵 Intent: Task 006 - Bootstrap Icons + tooltips + Japanese form-text. -->

<template>
  <div class="d-flex flex-column mb-2 p-4 border border-2 rounded">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <p class="lead mb-0">
        <i class="bi bi-people-fill me-2 text-primary" aria-hidden="true"></i>
        テナント管理
      </p>
      <div class="d-flex align-items-center">
        <label class="me-2 small">
          <i class="bi bi-funnel-fill me-1" aria-hidden="true"></i>
          Status:
        </label>
        <select
          class="form-select form-select-sm"
          style="width: auto;"
          :value="statusFilter"
          @change="onFilterChange($event.target.value)"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="表示するテナントのステータスで絞り込みます。"
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
    </div>

    <!-- loading indicator -->
    <div v-if="loading" class="text-center my-3">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <span class="ms-2">Loading tenants...</span>
    </div>

    <!-- error message -->
    <div v-if="errorMessage" class="alert alert-danger alert-dismissible" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- empty state -->
    <div v-if="!loading && filteredTenants.length === 0" class="text-muted text-center my-3">
      <i class="bi bi-inbox me-1" aria-hidden="true"></i>
      テナントが登録されていません。
    </div>

    <!-- tenant table -->
    <div v-if="!loading && filteredTenants.length > 0" class="table-responsive">
      <table class="table table-hover table-sm align-middle">
        <thead>
          <tr>
            <th>Name</th>
            <th>Event ID</th>
            <th>Inlet Strategy</th>
            <th>Status</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="tenant in filteredTenants"
            :key="tenant.event_id"
            :class="{ 'table-active': isSelected(tenant) }"
            style="cursor: pointer;"
            @click="onSelectTenant(tenant)"
          >
            <td>{{ tenant.name }}</td>
            <td><code>{{ tenant.event_id }}</code></td>
            <td>{{ formatStrategy(tenant.inlet_strategy) }}</td>
            <td>
              <span
                class="badge"
                :class="tenant.status === 'active' ? 'bg-success' : 'bg-secondary'"
              >
                {{ tenant.status }}
              </span>
            </td>
            <td class="text-end">
              <a
                v-if="tenantAdminUrl(tenant)"
                :href="tenantAdminUrl(tenant)"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-sm btn-outline-primary me-1"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="テナント管理画面を新しいタブで開きます。"
                data-testid="tenant-admin-link"
                @click.stop
              >
                <i class="bi bi-box-arrow-up-right me-1" aria-hidden="true"></i>
                Open
              </a>
              <button
                type="button"
                class="btn btn-sm"
                :class="tenant.status === 'active' ? 'btn-outline-warning' : 'btn-outline-success'"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                :title="tenant.status === 'active'
                  ? 'このテナントの待機室を無効化します。'
                  : 'このテナントの待機室を有効化します。'"
                @click.stop="onToggleStatus(tenant)"
                :disabled="togglingId === tenant.event_id"
              >
                <i
                  :class="['bi', tenant.status === 'active' ? 'bi-pause-fill' : 'bi-play-fill', 'me-1']"
                  aria-hidden="true"
                ></i>
                {{ tenant.status === 'active' ? 'Disable' : 'Enable' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="form-text">
      行をクリックすると下部のフォームで編集できます。Open ボタンでテナント管理画面を新しいタブで開けます。Enable / Disable は待機室全体の有効状態を切り替えます。
    </div>

    <!-- refresh button -->
    <div class="d-flex justify-content-end mt-2">
      <button
        type="button"
        class="btn btn-sm btn-outline-primary rounded"
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title="サーバから最新のテナント一覧を再取得します。"
        @click="onRefresh"
        :disabled="loading"
      >
        <i class="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
        Refresh
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useTenantStore } from "../stores/tenant";
import { useBootstrapTooltips } from "../composables/useBootstrapTooltips";
import { buildTenantAdminUrl } from "../composables/useEndpoints";

// 🔵 Intent: Task 006 - bootstrap tooltips when this component mounts.
useBootstrapTooltips();

const emit = defineEmits(["tenant-selected"]);

const tenantStore = useTenantStore();
const { tenants, loading, statusFilter, selectedTenant } = storeToRefs(tenantStore);

const errorMessage = ref("");
const togglingId = ref(null);

const filteredTenants = computed(() => {
  if (statusFilter.value === "all") {
    return tenants.value;
  }
  return tenants.value.filter((t) => t.status === statusFilter.value);
});

function isSelected(tenant) {
  return (
    selectedTenant.value &&
    selectedTenant.value.event_id === tenant.event_id
  );
}

function formatStrategy(strategy) {
  if (!strategy) return "-";
  return strategy.charAt(0).toUpperCase() + strategy.slice(1);
}

// tenant_code is the canonical subdomain label per ADR-0005, normalized
// to lowercase. The Private API populates it on create (defaulting to
// event_id when omitted), so any tenant returned from the BFF should
// have it set. Returns '' when tenant_code or TENANT_ADMIN_BASE_DOMAIN
// is missing, which hides the link via the v-if in the template.
function tenantAdminUrl(tenant) {
  return buildTenantAdminUrl(tenant.tenant_code);
}

function onFilterChange(value) {
  tenantStore.setStatusFilter(value);
  onRefresh();
}

function onSelectTenant(tenant) {
  tenantStore.selectTenant(tenant);
  emit("tenant-selected", tenant);
}

async function onToggleStatus(tenant) {
  errorMessage.value = "";
  togglingId.value = tenant.event_id;
  const newStatus = tenant.status === "active" ? "inactive" : "active";
  try {
    await tenantStore.setTenantStatus(tenant.event_id, newStatus);
  } catch (error) {
    errorMessage.value = error.message || "Failed to update tenant status.";
  } finally {
    togglingId.value = null;
  }
}

async function onRefresh() {
  errorMessage.value = "";
  try {
    await tenantStore.fetchTenants();
  } catch (error) {
    errorMessage.value = error.message || "Failed to fetch tenants.";
  }
}
</script>
